<template>
  <div class="bg-gray-50 p-4 space-y-4">

    <!-- Toolbar -->
    <div class="flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-4 py-3">
      <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Metric</span>
      <select
        :value="selectedMetric"
        @change="openMetric(Number($event.target.value))"
        class="px-3 py-1.5 text-xs font-medium text-gray-900 border-0 bg-gray-50 rounded focus:ring-1 focus:ring-blue-500 cursor-pointer"
        style="min-width: 300px;"
      >
        <option :value="null" disabled>Select a metric…</option>
        <option v-for="metric in filteredMetrics" :key="metric.id" :value="metric.id">
          {{ metric.id }} — {{ metric.name }} ({{ metricTaskCounts[metric.id] || 0 }} steps)
        </option>
      </select>
      <button @click="showAddMetricsModal = true" class="w-7 h-7 flex items-center justify-center rounded bg-gray-50 hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="Add metric workflow">
        <font-awesome-icon :icon="['fas', 'plus']" style="font-size: 10px;" />
      </button>
      <div v-if="selectedMetric" class="flex gap-1 p-0.5 bg-gray-100 rounded ml-auto">
        <button
          @click="viewMode = 'actions'"
          class="px-3 py-1 text-xs font-medium rounded"
          :class="viewMode === 'actions' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
        >Actions</button>
        <button
          @click="viewMode = 'work'"
          class="px-3 py-1 text-xs font-medium rounded"
          :class="viewMode === 'work' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
        >Work Types</button>
      </div>
    </div>

    <!-- Workflow visualization -->
    <div v-if="selectedMetric && workflowTasks.length">
      <table class="border-collapse">
        <!-- Task headers -->
        <thead>
          <tr>
            <template v-for="(task, i) in workflowTasks" :key="'h-'+task.id">
              <th class="align-top p-0 pb-2">
                <div class="group/task">
                  <div class="flex items-center justify-center gap-1 h-5 mb-1 opacity-0 group-hover/task:opacity-100 transition-opacity">
                    <button v-if="i > 0" @click="moveTask(i, -1)" class="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600" title="Move left">
                      <font-awesome-icon :icon="['fas', 'chevron-left']" style="font-size: 8px;" />
                    </button>
                    <button @click="removeTask(task)" class="w-5 h-5 flex items-center justify-center rounded hover:bg-red-100 text-gray-400 hover:text-red-500" title="Remove task">
                      <font-awesome-icon :icon="['fas', 'trash']" style="font-size: 8px;" />
                    </button>
                    <button v-if="i < workflowTasks.length - 1" @click="moveTask(i, 1)" class="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600" title="Move right">
                      <font-awesome-icon :icon="['fas', 'chevron-right']" style="font-size: 8px;" />
                    </button>
                  </div>
                  <div class="text-[10px] font-medium text-gray-400 text-center mb-0.5">Order {{ task.task_order }}</div>
                  <div class="flex items-center gap-2 px-3 py-2 bg-white rounded border border-gray-200 whitespace-nowrap h-9">
                    <div class="w-4 h-4 rounded flex items-center justify-center" :style="{ backgroundColor: task.task_type_color || '#3B82F6' }">
                      <font-awesome-icon :icon="['fas', task.task_type_icon || 'tasks']" class="text-white" style="font-size: 8px;" />
                    </div>
                    <input
                      v-if="editingTaskTypeId === task.task_type"
                      ref="taskNameInput"
                      v-model="editingTaskTypeName"
                      type="text"
                      class="text-xs font-semibold text-gray-900 border border-blue-400 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                      style="min-width: 90px;"
                      @keyup.enter="saveTaskTypeName(task)"
                      @keyup.esc="cancelTaskTypeName"
                      @blur="saveTaskTypeName(task)"
                    />
                    <span
                      v-else
                      class="text-xs font-semibold text-gray-900 cursor-text hover:underline decoration-dotted"
                      title="Click to rename task"
                      @click="startTaskTypeName(task)"
                    >{{ task.task_type_name }}</span>
                    <span class="text-[10px] text-gray-400 font-mono">{{ task.task_type }}</span>
                    <span
                      v-if="sharedMetricsFor(task.task_type).length"
                      class="shared-badge text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap relative cursor-default"
                    >
                      <font-awesome-icon :icon="['fas', 'layer-group']" style="font-size: 7px;" />
                      Shared · {{ sharedMetricsFor(task.task_type).length }}
                      <span class="shared-tooltip">
                        <span class="font-semibold">Shared with:</span>
                        <span v-for="name in sharedMetricsFor(task.task_type)" :key="name" class="block">· {{ name }}</span>
                        <span class="block mt-1 text-amber-500">Renaming or editing actions changes those programs too.</span>
                      </span>
                    </span>
                    <button @click="openAddActionModal(task)" class="w-5 h-5 flex items-center justify-center rounded bg-gray-100 hover:bg-blue-100 text-gray-400 hover:text-blue-600 ml-1" title="Add action">
                      <font-awesome-icon :icon="['fas', 'plus']" style="font-size: 9px;" />
                    </button>
                  </div>
                </div>
              </th>
              <th v-if="i < workflowTasks.length - 1" class="px-2 align-middle">
                <font-awesome-icon :icon="['fas', 'arrow-right']" class="text-gray-300" style="font-size: 9px;" />
              </th>
            </template>
            <th class="align-top p-0 pb-2">
              <div class="ml-2">
                <div class="h-5 mb-1"></div>
                <div class="flex items-center gap-2 px-3 py-2 bg-white rounded border border-dashed border-gray-300 hover:border-gray-400 text-gray-400 hover:text-gray-600 whitespace-nowrap cursor-pointer h-9" @click="showAddTaskModal = true">
                  <div class="w-4 h-4 rounded flex items-center justify-center bg-gray-200">
                    <font-awesome-icon :icon="['fas', 'plus']" class="text-gray-500" style="font-size: 8px;" />
                  </div>
                  <span class="text-xs font-semibold">Add task</span>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <!-- Action groups -->
        <tbody v-if="viewMode === 'actions'">
          <tr>
            <template v-for="(task, i) in workflowTasks" :key="'b-'+task.id">
              <td class="align-top p-0">
                <div v-if="taskActionGroups[task.task_type]" class="space-y-2">
                  <div
                    v-for="group in taskActionGroups[task.task_type]"
                    :key="group.key"
                    class="rounded border border-gray-200 bg-white overflow-hidden"
                  >
                    <!-- Instruction -->
                    <div class="px-2.5 py-1.5 border-b border-gray-100 bg-gray-50">
                      <span class="text-[11px] font-medium px-1.5 py-0.5 rounded-full" :class="instructionColor(group.instruction_type)">{{ group.instruction_name }}</span>
                    </div>

                    <!-- Config -->
                    <div v-if="group.config" class="px-4 py-1.5 border-b border-gray-100">
                      <div class="text-[9px] font-medium text-gray-400 uppercase tracking-wide mb-1">Config</div>
                      <div
                        v-for="action in group.actions.slice(0, 1)"
                        :key="'cfg-'+action.id"
                      >
                        <template v-for="(val, key) in group.config" :key="key">
                          <!-- Scalar value -->
                          <div
                            v-if="typeof val !== 'object' || val === null"
                            class="group/cfg flex items-center gap-1 text-[11px] leading-relaxed py-0.5 -mx-1 px-1 rounded cursor-pointer hover:bg-gray-50"
                            @click="openConfigEditor(action)"
                          >
                            <span class="text-gray-400">{{ key }}:</span>
                            <span class="ml-1 font-medium text-gray-800 flex-1">{{ val === null ? 'null' : val }}</span>
                            <font-awesome-icon :icon="['fas', 'pen']" class="text-gray-300 opacity-0 group-hover/cfg:opacity-100" style="font-size: 8px;" />
                          </div>
                          <!-- Nested object -->
                          <div v-else class="py-0.5 cursor-pointer" @click="openConfigEditor(action)">
                            <div class="text-[11px] text-gray-400 mb-0.5">{{ key }}:</div>
                            <div class="ml-3 border-l-2 border-gray-100 pl-2">
                              <div
                                v-for="(subVal, subKey) in val"
                                :key="subKey"
                                class="group/cfg flex items-center gap-1 text-[11px] leading-relaxed py-0.5 -mx-1 px-1 rounded hover:bg-gray-50"
                              >
                                <span class="text-gray-400">{{ subKey }}:</span>
                                <span class="ml-1 font-medium text-gray-800 flex-1">{{ subVal }}</span>
                                <font-awesome-icon :icon="['fas', 'pen']" class="text-gray-300 opacity-0 group-hover/cfg:opacity-100" style="font-size: 8px;" />
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>

                    <!-- Actions -->
                    <div class="px-4 py-1.5">
                      <div class="text-[9px] font-medium text-gray-400 uppercase tracking-wide mb-1">Actions</div>
                      <div
                        v-for="action in group.actions"
                        :key="action.id"
                        class="group/action flex items-center gap-1 leading-relaxed py-0.5 -mx-1 px-1 rounded hover:bg-gray-50"
                      >
                        <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="instructionDot(group.instruction_type)"></span>
                        <span class="text-[11px] text-gray-700 flex-1">{{ action.name }}</span>
                        <span class="text-[9px] text-gray-400 font-mono flex-shrink-0">{{ action.id }}</span>
                        <button @click="openEditActionModal(action)" class="w-4 h-4 flex items-center justify-center text-gray-300 hover:text-blue-600 opacity-0 group-hover/action:opacity-100 flex-shrink-0" title="Edit action">
                          <font-awesome-icon :icon="['fas', 'pen']" style="font-size: 8px;" />
                        </button>
                        <button @click="removeAction(action)" class="w-4 h-4 flex items-center justify-center text-gray-300 hover:text-red-500 opacity-0 group-hover/action:opacity-100 flex-shrink-0" title="Remove">
                          <font-awesome-icon :icon="['fas', 'times']" style="font-size: 8px;" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
              <td v-if="i < workflowTasks.length - 1"></td>
            </template>
          </tr>
        </tbody>

        <!-- Work types -->
        <tbody v-else>
          <tr>
            <template v-for="(task, i) in workflowTasks" :key="'w-'+task.id">
              <td class="align-top p-0">
                <div class="rounded border border-gray-200 bg-white overflow-hidden">
                  <div class="px-4 py-1.5">
                    <div class="flex items-center gap-1 mb-1">
                      <div class="text-[9px] font-medium text-gray-400 uppercase tracking-wide flex-1">Work Types</div>
                      <button @click="openAddWorkTypeModal(task)" class="w-4 h-4 flex items-center justify-center rounded bg-gray-100 hover:bg-blue-100 text-gray-400 hover:text-blue-600" title="Add work type">
                        <font-awesome-icon :icon="['fas', 'plus']" style="font-size: 8px;" />
                      </button>
                    </div>
                    <div v-if="taskWorkTypesMap[task.task_type] && taskWorkTypesMap[task.task_type].length">
                      <div
                        v-for="wt in taskWorkTypesMap[task.task_type]"
                        :key="wt.task_work_type_id"
                        class="group/wt flex items-center gap-1 leading-relaxed py-0.5 -mx-1 px-1 rounded hover:bg-gray-50"
                      >
                        <span class="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-blue-500"></span>
                        <span class="text-[11px] text-gray-700 flex-1">{{ wt.display_name || wt.name }}</span>
                        <button @click="removeWorkType(wt)" class="w-4 h-4 flex items-center justify-center text-gray-300 hover:text-red-500 opacity-0 group-hover/wt:opacity-100 flex-shrink-0" title="Remove">
                          <font-awesome-icon :icon="['fas', 'times']" style="font-size: 8px;" />
                        </button>
                      </div>
                    </div>
                    <div v-else class="text-[11px] text-gray-400 py-1">No work types</div>
                  </div>
                </div>
              </td>
              <td v-if="i < workflowTasks.length - 1"></td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <div v-else-if="selectedMetric" class="text-center py-8 text-xs text-gray-400">No tasks configured for this metric</div>

    <!-- Config JSON Editor -->
    <transition name="modal">
      <div v-if="editingConfig" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-lg">
          <div class="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <h3 class="text-sm font-semibold text-gray-900">Edit Config — {{ editingConfig.action.name }}</h3>
            <button @click="editingConfig = null" class="text-gray-400 hover:text-gray-600">&times;</button>
          </div>
          <div class="p-4">
            <textarea
              v-model="editingConfig.json"
              rows="12"
              class="w-full px-3 py-2 text-xs font-mono border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              :class="editingConfigError ? 'border-red-400' : ''"
            ></textarea>
            <p v-if="editingConfigError" class="text-[10px] text-red-500 mt-1">{{ editingConfigError }}</p>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="editingConfig = null" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="saveConfigEdit" :disabled="!!editingConfigError" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Save</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Add Work Type Modal -->
    <transition name="modal">
      <div v-if="showAddWorkTypeModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-sm w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">Add Work Type to {{ addWorkTypeTask?.task_type_name }}</h3>
          </div>
          <div class="p-4">
            <label class="block text-xs font-medium text-gray-700 mb-1">Work Type</label>
            <select v-model="selectedNewWorkTypeId" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option :value="null">-- Select Work Type --</option>
              <option v-for="wt in availableWorkTypesForTask" :key="wt.id" :value="wt.id">{{ wt.display_name || wt.name }}</option>
            </select>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="closeAddWorkTypeModal" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="assignWorkType" :disabled="!selectedNewWorkTypeId" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Add</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Add Action Modal -->
    <transition name="modal">
      <div v-if="showAddActionModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">{{ editingActionId ? 'Edit Action' : 'Add Action to ' + (addActionTaskType?.task_type_name || '') }}</h3>
          </div>
          <div class="p-4 space-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Name</label>
              <input v-model="newAction.name" type="text" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500" placeholder="Action name" />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <input v-model="newAction.description" type="text" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500" placeholder="Description" />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Instruction Type</label>
              <select v-model="newAction.task_instruction_type" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                <option :value="null">-- Select --</option>
                <option v-for="inst in instructionTypes" :key="inst.id" :value="inst.id">{{ inst.name }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Config (JSON)</label>
              <div v-if="newActionConfigHint" class="mb-1 p-2 bg-blue-50 border border-blue-200 rounded">
                <p class="text-[10px] text-blue-800 font-medium mb-0.5">Required config for this instruction type:</p>
                <pre class="text-[10px] text-blue-700 font-mono whitespace-pre-wrap">{{ newActionConfigHint }}</pre>
              </div>
              <textarea v-model="newAction.config_json" rows="5" class="w-full px-2 py-1 text-xs border border-gray-300 rounded font-mono focus:ring-1 focus:ring-blue-500 focus:border-blue-500" placeholder="{}" />
              <p v-if="newActionConfigError" class="text-[10px] text-red-500 mt-0.5">{{ newActionConfigError }}</p>
            </div>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="closeAddActionModal" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="saveAction" :disabled="!newAction.name || !newAction.task_instruction_type || !!newActionConfigError" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">{{ editingActionId ? 'Save Changes' : 'Add Action' }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modals -->
    <transition name="modal">
      <div v-if="showAddTaskModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-sm w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">Add Task to Workflow</h3>
          </div>
          <div class="p-4 space-y-3">
            <!-- Toggle -->
            <div class="flex gap-1 p-0.5 bg-gray-100 rounded">
              <button
                @click="addTaskMode = 'existing'"
                class="flex-1 px-3 py-1 text-xs font-medium rounded"
                :class="addTaskMode === 'existing' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
              >Existing</button>
              <button
                @click="addTaskMode = 'new'"
                class="flex-1 px-3 py-1 text-xs font-medium rounded"
                :class="addTaskMode === 'new' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
              >Create new</button>
            </div>

            <!-- Existing task type -->
            <div v-if="addTaskMode === 'existing'">
              <label class="block text-xs font-medium text-gray-700 mb-1">Task Type</label>
              <select v-model="newTask.task_type" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                <option :value="null">-- Select Task Type --</option>
                <option v-for="taskType in availableTaskTypes" :key="taskType.id" :value="taskType.id">{{ taskType.name }}</option>
              </select>
            </div>

            <!-- New task type -->
            <div v-else class="space-y-3">
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input v-model="newTaskType.name" type="text" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500" placeholder="Task type name" />
              </div>
              <div class="flex gap-3">
                <div class="flex-1">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Color</label>
                  <div class="flex items-center gap-2">
                    <input v-model="newTaskType.color" type="color" class="h-8 w-10 border border-gray-300 rounded cursor-pointer" />
                    <input v-model="newTaskType.color" type="text" class="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500" placeholder="#3B82F6" />
                  </div>
                </div>
                <div class="w-24">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Icon</label>
                  <input v-model="newTaskType.icon" type="text" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500" placeholder="tasks" />
                </div>
              </div>
            </div>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="closeAddTaskModal" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button
              v-if="addTaskMode === 'existing'"
              @click="addTask"
              :disabled="!newTask.task_type"
              class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
            >Add Task</button>
            <button
              v-else
              @click="createAndAddTask"
              :disabled="!newTaskType.name"
              class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
            >Create & Add</button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="showAddWorkModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-sm w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">Add Work Template</h3>
          </div>
          <div class="p-4 space-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Work Type</label>
              <select v-model="newWorkTemplate.work_type" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                <option :value="null">-- Select Work Type --</option>
                <option v-for="workType in availableWorkTypes" :key="workType.id" :value="workType.id">{{ workType.display_name || workType.name }}</option>
              </select>
            </div>
            <label class="flex items-center gap-1.5 text-xs text-gray-700">
              <input v-model="newWorkTemplate.is_required" type="checkbox" class="h-3 w-3 rounded border-gray-300">
              Required
            </label>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="closeAddWorkModal" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="addWorkTemplate" :disabled="!newWorkTemplate.work_type" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Add Template</button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="showCloneModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-sm w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">Clone Workflow</h3>
          </div>
          <div class="p-4">
            <label class="block text-xs font-medium text-gray-700 mb-1">Copy workflow from:</label>
            <select v-model="cloneSourceMetric" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option :value="null">-- Select Source Metric --</option>
              <option v-for="metric in metrics.filter(m => m.id !== selectedMetric)" :key="metric.id" :value="metric.id">{{ metric.name }}</option>
            </select>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="closeCloneModal" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="cloneWorkflow" :disabled="!cloneSourceMetric" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Clone Workflow</button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="showAddMetricsModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">Add Metric Workflow</h3>
            <p class="text-[11px] text-gray-500 mt-0.5">Pick a metric and assign its task sequence</p>
          </div>
          <div class="p-4 space-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Metric</label>
              <select v-model="addMetricId" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                <option :value="null">-- Select Metric --</option>
                <option v-for="m in unwiredMetrics" :key="m.id" :value="m.id">{{ m.id }} — {{ m.name }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Task types (in order)</label>
              <MultiSelectDropdown v-model="addMetricTaskTypes" :options="taskTypeOptions" placeholder="Select task types…" searchable />
              <div v-if="addMetricTaskTypes.length" class="mt-2 flex flex-wrap gap-1.5">
                <span v-for="(id, i) in addMetricTaskTypes" :key="id" class="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  {{ i + 1 }}. {{ getTaskTypeName(id) }}
                  <button @click="addMetricTaskTypes = addMetricTaskTypes.filter(x => x !== id)" class="text-gray-400 hover:text-gray-600">
                    <font-awesome-icon :icon="['fas', 'times']" style="font-size: 8px;" />
                  </button>
                </span>
              </div>
            </div>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex items-center justify-between">
            <span class="text-[11px] text-gray-500">{{ addMetricTaskTypes.length }} task types</span>
            <div class="flex gap-2">
              <button @click="closeAddMetricsModal" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
              <button @click="addMetricWorkflow" :disabled="!addMetricId || addMetricTaskTypes.length === 0" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Create Workflow</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Confirm Modal (generic + shared) -->
    <transition name="modal">
      <div v-if="confirmModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-sm w-full">
          <div class="px-4 py-3 border-b rounded-t-lg flex items-center gap-2"
            :class="confirmModal.variant === 'danger' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'"
          >
            <div class="w-6 h-6 rounded-full flex items-center justify-center"
              :class="confirmModal.variant === 'danger' ? 'bg-red-100' : 'bg-amber-100'"
            >
              <font-awesome-icon
                :icon="['fas', confirmModal.variant === 'danger' ? 'trash' : 'triangle-exclamation']"
                :class="confirmModal.variant === 'danger' ? 'text-red-600' : 'text-amber-600'"
                style="font-size: 11px;"
              />
            </div>
            <h3 class="text-sm font-semibold" :class="confirmModal.variant === 'danger' ? 'text-red-900' : 'text-amber-900'">{{ confirmModal.title }}</h3>
          </div>
          <div class="px-4 py-4">
            <p class="text-sm text-gray-700" :class="{ 'mb-3': confirmModal.programs }">{{ confirmModal.message }}</p>
            <div v-if="confirmModal.programs" class="bg-amber-50 border border-amber-100 rounded px-3 py-2">
              <p style="font-size: 11px;" class="font-medium text-amber-800 mb-1">Affected programs:</p>
              <ul class="space-y-0.5">
                <li v-for="name in confirmModal.programs" :key="name" style="font-size: 12px;" class="text-amber-700 flex items-center gap-1.5">
                  <span class="w-1 h-1 rounded-full bg-amber-400"></span>
                  {{ name }}
                </li>
              </ul>
            </div>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="resolveConfirmModal(false)" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="resolveConfirmModal(true)" class="px-3 py-1.5 text-xs text-white rounded"
              :class="confirmModal.variant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'"
            >{{ confirmModal.confirmLabel || 'Continue' }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Toast Notification -->
    <transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <div v-if="toast" class="fixed bottom-4 right-4 z-50 max-w-sm">
        <div class="flex items-start gap-2 px-4 py-3 rounded-lg shadow-lg border"
          :class="toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'"
        >
          <font-awesome-icon :icon="['fas', toast.type === 'error' ? 'circle-exclamation' : 'check-circle']" class="mt-0.5" style="font-size: 13px;" />
          <div class="flex-1">
            <p class="text-xs font-medium">{{ toast.title }}</p>
            <p v-if="toast.detail" class="text-xs mt-0.5 opacity-80">{{ toast.detail }}</p>
          </div>
          <button @click="toast = null" class="text-gray-400 hover:text-gray-600">
            <font-awesome-icon :icon="['fas', 'times']" style="font-size: 10px;" />
          </button>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="confirmDeleteMetric" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-sm">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">Delete Workflow</h3>
          </div>
          <div class="p-4">
            <p class="text-[13px] text-gray-700">Remove all task assignments from <span class="font-semibold">{{ confirmDeleteMetric.name }}</span>?</p>
            <p class="text-[11px] text-gray-500 mt-1">This deletes {{ metricTaskCounts[confirmDeleteMetric.id] || 0 }} task step(s) and cannot be undone.</p>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="confirmDeleteMetric = null" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="deleteMetricWorkflow" class="px-3 py-1.5 text-xs text-white bg-red-600 rounded hover:bg-red-700">Delete</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import draggable from 'vuedraggable';
import MultiSelectDropdown from '@/components/widgetcomponents/library/MultiSelectDropdown.vue';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  components: { draggable, FontAwesomeIcon, MultiSelectDropdown },

  data() {
    return {
      metrics: [],
      taskTypes: [],
      workTypes: [],
      selectedMetric: null,
      metricSearch: '',
      metricTaskCounts: {},
      taskTypeMetricIds: {},   // task_type_id -> Set(metric_id) for shared-step detection
      workflowTasks: [],
      workTemplates: [],
      taskActionsMap: {},
      selectedWorkflowTask: null,
      // Inline task-type rename (writes TaskType.name via /api/tasktypes/{id}/)
      editingTaskTypeId: null,
      editingTaskTypeName: '',

      viewMode: 'actions',
      taskWorkTypesMap: {},

      // Add work type to task
      showAddWorkTypeModal: false,
      addWorkTypeTask: null,
      selectedNewWorkTypeId: null,

      // Edit config
      editingConfig: null, // { action, config }

      // Add / edit action
      showAddActionModal: false,
      addActionTaskType: null,
      editingActionId: null,
      newAction: { name: '', description: '', task_instruction_type: null, config_json: '{}' },
      instructionTypes: [],

      // Modals
      showAddTaskModal: false,
      addTaskMode: 'existing',
      newTaskType: { name: '', color: '#3B82F6', icon: 'tasks' },
      showAddWorkModal: false,
      showCloneModal: false,
      showAddMetricsModal: false,
      confirmDeleteMetric: null,
      newTask: { task_type: null },
      newWorkTemplate: { work_type: null, is_required: true },
      cloneSourceMetric: null,
      addMetricId: null,
      addMetricTaskTypes: [],

      // Config editor
      confirmModal: null,
      toast: null,
      toastTimer: null,
    };
  },

  watch: {
    $route: 'syncRouteSelection',
    'newAction.task_instruction_type'(newType) {
      if (!newType) return;
      const current = this.newAction.config_json?.trim();
      if (current && current !== '{}') return;
      if (this.newActionConfigHint) {
        this.newAction.config_json = this.newActionConfigHint;
      }
    },
  },
  computed: {
    filteredMetrics() {
      let list = this.metrics.filter(m => this.metricTaskCounts[m.id] > 0);
      if (this.metricSearch) {
        const q = this.metricSearch.toLowerCase();
        list = list.filter(m => m.name.toLowerCase().includes(q) || String(m.id).includes(q));
      }
      return list;
    },
    unwiredMetrics() {
      return this.metrics.filter(m => !this.metricTaskCounts[m.id]);
    },
    taskTypeOptions() {
      return this.taskTypes.map(tt => ({ value: tt.id, label: tt.name }));
    },
    availableTaskTypes() {
      const used = this.workflowTasks.map(t => t.task_type);
      return this.taskTypes.filter(tt => !used.includes(tt.id));
    },
    availableWorkTypes() {
      const used = this.workTemplates.map(t => t.work_type);
      return this.workTypes.filter(wt => !used.includes(wt.id));
    },
    availableWorkTypesForTask() {
      if (!this.addWorkTypeTask) return [];
      const assigned = (this.taskWorkTypesMap[this.addWorkTypeTask.task_type] || []).map(wt => wt.id);
      return this.workTypes.filter(wt => !assigned.includes(wt.id));
    },
    editingConfigError() {
      if (!this.editingConfig?.json) return null;
      try { JSON.parse(this.editingConfig.json); return null; } catch (e) { return e.message; }
    },
    newActionConfigError() {
      if (!this.newAction.config_json || this.newAction.config_json.trim() === '') return null;
      try { JSON.parse(this.newAction.config_json); return null; } catch (e) { return 'Invalid JSON'; }
    },
    newActionConfigHint() {
      const hints = {
        1: '{\n  "default_due_days": 7,\n  "date_picker": {\n    "enabled": true,\n    "target": "due_date",\n    "label": "Next call due date",\n    "required": false,\n    "default_days": 7\n  }\n}',
        6: '{\n  "default_snooze_days": 1,\n  "date_picker": {\n    "enabled": true,\n    "target": "snooze_until",\n    "label": "Snooze until",\n    "required": false\n  }\n}',
        9: '{\n  "max_repeats": null,\n  "repeat_strategy": {\n    "type": "default",\n    "interval_days": 7\n  }\n}',
        10: '{\n  "target_metric_slug": "METRIC_SLUG",\n  "close_current": true\n}',
        11: '{\n  "skip_count": 2,\n  "default_due_days": 7\n}',
        15: '{\n  "rediscovery_days": 30\n}',
        16: '{\n  "default_push_days": 2\n}',
      };
      return hints[this.newAction.task_instruction_type] || null;
    },
    taskActionGroups() {
      const result = {};
      for (const [taskTypeId, actions] of Object.entries(this.taskActionsMap)) {
        const groups = {};
        for (const a of actions) {
          const configKey = JSON.stringify(a.config || {});
          const key = `${a.task_instruction_type}-${configKey}`;
          if (!groups[key]) {
            groups[key] = {
              key,
              instruction_type: a.task_instruction_type,
              instruction_name: a.instruction_name || '—',
              config: a.config && Object.keys(a.config).length ? a.config : null,
              count: 0,
              actions: [],
            };
          }
          groups[key].count++;
          groups[key].actions.push(a);
        }
        result[taskTypeId] = Object.values(groups).sort((a, b) => b.count - a.count);
      }
      return result;
    },
  },

  methods: {
    // --- Inline task-type rename ----------------------------------------
    // Renaming here edits the shared TaskType.name (same record the Task
    // Configuration tab edits), so the new name propagates everywhere the
    // task type is shown. PATCH /api/tasktypes/{id}/.
    startTaskTypeName(task) {
      this.editingTaskTypeId = task.task_type;
      this.editingTaskTypeName = task.task_type_name || '';
      this.$nextTick(() => {
        const el = Array.isArray(this.$refs.taskNameInput) ? this.$refs.taskNameInput[0] : this.$refs.taskNameInput;
        if (el) { el.focus(); el.select(); }
      });
    },
    cancelTaskTypeName() {
      this.editingTaskTypeId = null;
      this.editingTaskTypeName = '';
    },
    async saveTaskTypeName(task) {
      // Guard: only act while editing this task; ignore blur after esc/save.
      if (this.editingTaskTypeId !== task.task_type) return;
      const newName = (this.editingTaskTypeName || '').trim();
      const id = this.editingTaskTypeId;
      this.editingTaskTypeId = null;
      if (!newName || newName === task.task_type_name) return;
      // Shared-step guard: this renames the single shared TaskType record, so
      // it changes every program that reuses it (Pylon #114). Make that
      // explicit before writing.
      const shared = this.sharedMetricsFor(id);
      if (shared.length) {
        const ok = await this.showConfirm({
          title: 'Shared task type',
          message: `"${task.task_type_name}" is shared with ${shared.length} other program(s). Renaming it will change the name in those programs too.`,
          programs: shared,
        });
        if (!ok) return;
      }
      try {
        await axios.patch(`/api/tasktypes/${id}/`, { name: newName });
        this.workflowTasks.forEach((t) => {
          if (t.task_type === id) t.task_type_name = newName;
        });
        const tt = this.taskTypes.find((t) => t.id === id);
        if (tt) tt.name = newName;
      } catch (e) {
        console.error(e);
        this.showToast('Error renaming task', e.response?.data?.detail);
      }
    },
    async fetchMetrics() {
      try {
        const { data } = await axios.get('/api/metrics/');
        this.metrics = data.sort((a, b) => a.id - b.id);
        this.syncRouteSelection();
      } catch (e) { console.error(e); }
    },
    async fetchTaskTypes() {
      try { const { data } = await axios.get('/api/tasktypes/'); this.taskTypes = data; } catch (e) { console.error(e); }
    },
    async fetchWorkTypes() {
      try { const { data } = await axios.get('/api/worktypes/'); this.workTypes = data; } catch (e) { console.error(e); }
    },
    async fetchAllMetricTaskTypes() {
      try {
        const { data } = await axios.get('/api/metrictasktypes/');
        const counts = {};
        // task_type_id -> Set(metric_id) so we can flag steps reused across
        // programs. A TaskType (and its TaskActionTypes) is a single shared
        // record — renaming it or editing its actions on one program mutates
        // every program that reuses it (Nesha's SNF->Post-Discharge IP
        // bleed-through, Pylon #114). This map drives the "Shared" badge +
        // the rename/edit-action confirmations.
        const shared = {};
        data.forEach(mtt => {
          counts[mtt.metric] = (counts[mtt.metric] || 0) + 1;
          (shared[mtt.task_type] = shared[mtt.task_type] || new Set()).add(mtt.metric);
        });
        this.metricTaskCounts = counts;
        this.taskTypeMetricIds = shared;
        this.syncRouteSelection();
      } catch (e) { console.error(e); }
    },
    showConfirm({ title, message, programs, variant, confirmLabel }) {
      return new Promise(resolve => {
        this.confirmModal = { title, message, programs, variant: variant || 'warning', confirmLabel, resolve };
      });
    },
    resolveConfirmModal(result) {
      if (this.confirmModal?.resolve) this.confirmModal.resolve(result);
      this.confirmModal = null;
    },
    showToast(title, detail, type = 'error') {
      clearTimeout(this.toastTimer);
      this.toast = { title, detail, type };
      this.toastTimer = setTimeout(() => { this.toast = null; }, 5000);
    },
    sharedMetricsFor(taskTypeId) {
      const ids = this.taskTypeMetricIds[taskTypeId];
      if (!ids || ids.size <= 1) return [];
      const names = [];
      ids.forEach(mid => {
        const m = this.metrics.find(x => x.id === mid);
        names.push(m ? m.name : ('Metric ' + mid));
      });
      return names;
    },
    async selectMetric(metricId) {
      this.selectedMetric = metricId;
      this.workflowTasks = [];
      this.workTemplates = [];
      this.selectedWorkflowTask = null;
      try {
        const { data } = await axios.get('/api/metrictasktypes/', { params: { metric: metricId } });
        this.workflowTasks = data;
        this.fetchTaskActionsForWorkflow();
        this.fetchTaskWorkTypes();
      } catch (e) { console.error(e); }
      try {
        const { data } = await axios.get('/api/opportunityworktemplates/', { params: { metric: metricId } });
        this.workTemplates = data;
      } catch (e) { console.error(e); }
    },
    clearMetricSelection() {
      this.selectedMetric = null;
      this.workflowTasks = [];
      this.workTemplates = [];
      this.selectedWorkflowTask = null;
      this.taskActionsMap = {};
      this.taskWorkTypesMap = {};
    },
    syncRouteSelection() {
      const metricId = Number(this.$route.params.metricId);
      if (!metricId) {
        this.clearMetricSelection();
        return;
      }

      const isKnownMetric = this.metrics.some((metric) => metric.id === metricId);
      const hasWorkflow = this.filteredMetrics.some((metric) => metric.id === metricId);
      if (!isKnownMetric || !hasWorkflow) return;
      if (this.selectedMetric === metricId) return;
      this.selectMetric(metricId);
    },
    openMetric(metricId) {
      if (!metricId || Number(this.$route.params.metricId) === metricId) return;
      this.$router.push({
        name: 'AdminWorkflowMetricWorkflowDetail',
        params: { metricId: String(metricId) },
      });
    },
    async fetchTaskActionsForWorkflow() {
      this.taskActionsMap = {};
      await Promise.all(this.workflowTasks.map(async (task) => {
        try {
          const { data } = await axios.get('/api/taskactiontypes/', { params: { task_type: task.task_type, active_only: true } });
          this.taskActionsMap[task.task_type] = data.filter(a => a.active !== false);
        } catch (e) { console.error(e); }
      }));
    },

    instructionColor(t) {
      return { 1: 'bg-green-50 text-green-700', 2: 'bg-red-50 text-red-700', 6: 'bg-yellow-50 text-yellow-700', 9: 'bg-blue-50 text-blue-700', 10: 'bg-purple-50 text-purple-700', 15: 'bg-orange-50 text-orange-700' }[t] || 'bg-gray-100 text-gray-600';
    },
    instructionDot(t) {
      return { 1: 'bg-green-500', 2: 'bg-red-500', 6: 'bg-yellow-500', 9: 'bg-blue-500', 10: 'bg-purple-500', 15: 'bg-orange-500' }[t] || 'bg-gray-400';
    },
    // Work type CRUD
    async fetchTaskWorkTypes() {
      this.taskWorkTypesMap = {};
      await Promise.all(this.workflowTasks.map(async (task) => {
        try {
          const tt = this.taskTypes.find(t => t.id === task.task_type);
          if (tt && tt.work_types) {
            this.taskWorkTypesMap[task.task_type] = tt.work_types;
          }
        } catch (e) { console.error(e); }
      }));
    },
    openAddWorkTypeModal(task) {
      this.addWorkTypeTask = task;
      this.selectedNewWorkTypeId = null;
      this.showAddWorkTypeModal = true;
    },
    closeAddWorkTypeModal() {
      this.showAddWorkTypeModal = false;
      this.addWorkTypeTask = null;
      this.selectedNewWorkTypeId = null;
    },
    async assignWorkType() {
      try {
        await axios.post('/api/taskworktypes/bulk-assign/', {
          task_type_id: this.addWorkTypeTask.task_type,
          work_type_ids: [this.selectedNewWorkTypeId],
        });
        this.closeAddWorkTypeModal();
        await this.fetchTaskTypes();
        this.fetchTaskWorkTypes();
      } catch (e) { console.error(e); this.showToast('Error assigning work type', e.response?.data?.detail); }
    },
    async removeWorkType(wt) {
      const ok = await this.showConfirm({ title: 'Remove work type', message: `Remove "${wt.display_name || wt.name}"?`, variant: 'danger', confirmLabel: 'Remove' });
      if (!ok) return;
      try {
        await axios.delete(`/api/taskworktypes/${wt.task_work_type_id}/`);
        await this.fetchTaskTypes();
        this.fetchTaskWorkTypes();
      } catch (e) { console.error(e); }
    },

    // Config edit
    openConfigEditor(action) {
      this.editingConfig = {
        action,
        json: JSON.stringify(action.config || {}, null, 2),
      };
    },
    async saveConfigEdit() {
      if (this.editingConfigError) return;
      const { action, json } = this.editingConfig;
      try {
        const config = JSON.parse(json);
        await axios.patch(`/api/taskactiontypes/${action.id}/`, { config });
        this.editingConfig = null;
        this.fetchTaskActionsForWorkflow();
      } catch (e) {
        const configErrors = e.response?.data?.config;
        if (configErrors) {
          this.showToast('Config validation failed', Array.isArray(configErrors) ? configErrors.join(', ') : configErrors);
        } else {
          console.error(e);
          this.showToast('Error saving config', e.response?.data?.detail);
        }
      }
    },

    // Action CRUD
    openAddActionModal(task) {
      this.addActionTaskType = task;
      this.editingActionId = null;
      this.newAction = { name: '', description: '', task_instruction_type: null, config_json: '{}' };
      this.showAddActionModal = true;
    },
    openEditActionModal(action) {
      this.editingActionId = action.id;
      this.addActionTaskType = { task_type: action.task_type };
      this.newAction = {
        name: action.name,
        description: action.description || '',
        task_instruction_type: action.task_instruction_type,
        config_json: action.config && Object.keys(action.config).length
          ? JSON.stringify(action.config, null, 2)
          : '{}',
      };
      this.showAddActionModal = true;
    },
    closeAddActionModal() {
      this.showAddActionModal = false;
      this.addActionTaskType = null;
      this.editingActionId = null;
    },
    async saveAction() {
      const ttId = this.addActionTaskType?.task_type;
      const shared = ttId ? this.sharedMetricsFor(ttId) : [];
      if (shared.length) {
        const verb = this.editingActionId ? 'Editing' : 'Adding';
        const ok = await this.showConfirm({
          title: 'Shared task type',
          message: `This step is shared with ${shared.length} other program(s). ${verb} this action will apply to those programs too.`,
          programs: shared,
        });
        if (!ok) return;
      }
      return this.editingActionId ? this.saveEditedAction() : this.saveNewAction();
    },
    async saveNewAction() {
      if (this.newActionConfigError) return;
      let config = {};
      try { config = JSON.parse(this.newAction.config_json || '{}'); } catch { config = {}; }
      try {
        await axios.post('/api/taskactiontypes/', {
          task_type: this.addActionTaskType.task_type,
          name: this.newAction.name,
          description: this.newAction.description || this.newAction.name,
          task_instruction_type: this.newAction.task_instruction_type,
          config,
          active: true,
        });
        this.closeAddActionModal();
        this.fetchTaskActionsForWorkflow();
      } catch (e) {
        const configErrors = e.response?.data?.config;
        if (configErrors) {
          this.showToast('Config validation failed', Array.isArray(configErrors) ? configErrors.join(', ') : configErrors);
        } else {
          console.error(e);
          this.showToast('Error creating action', e.response?.data?.detail);
        }
      }
    },
    async saveEditedAction() {
      if (this.newActionConfigError) return;
      let config = {};
      try { config = JSON.parse(this.newAction.config_json || '{}'); } catch { config = {}; }
      try {
        await axios.patch(`/api/taskactiontypes/${this.editingActionId}/`, {
          name: this.newAction.name,
          description: this.newAction.description || this.newAction.name,
          task_instruction_type: this.newAction.task_instruction_type,
          config,
        });
        this.closeAddActionModal();
        this.fetchTaskActionsForWorkflow();
      } catch (e) {
        const configErrors = e.response?.data?.config;
        if (configErrors) {
          this.showToast('Config validation failed', Array.isArray(configErrors) ? configErrors.join(', ') : configErrors);
        } else {
          console.error(e);
          this.showToast('Error saving action', e.response?.data?.detail);
        }
      }
    },
    async removeAction(action) {
      const ok = await this.showConfirm({ title: 'Hide action', message: `Hide "${action.name}"? It will be deactivated, not deleted, so its recorded history is preserved.`, variant: 'danger', confirmLabel: 'Hide' });
      if (!ok) return;
      try {
        await axios.patch(`/api/taskactiontypes/${action.id}/`, { active: false });
        this.fetchTaskActionsForWorkflow();
      } catch (e) { console.error(e); this.showToast('Error hiding action', e.response?.data?.detail); }
    },
    async fetchInstructionTypes() {
      try { const { data } = await axios.get('/api/taskinstructiontypes/'); this.instructionTypes = data; } catch (e) { console.error(e); }
    },

    getTaskTypeName(id) {
      const tt = this.taskTypes.find(t => t.id === id);
      return tt ? tt.name : `Task Type ${id}`;
    },

    // Task reorder
    async moveTask(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.workflowTasks.length) return;
      const tasks = [...this.workflowTasks];
      [tasks[index], tasks[target]] = [tasks[target], tasks[index]];
      this.workflowTasks = tasks;
      await this.handleTaskReorder();
    },

    // CRUD
    async handleTaskReorder() {
      this.workflowTasks.forEach((t, i) => { t.task_order = i + 1; });
      try { await axios.post('/api/metrictasktypes/reorder/', { metric_id: this.selectedMetric, task_orders: this.workflowTasks.map(t => ({ id: t.id, order: t.task_order })) }); }
      catch (e) { console.error(e); this.selectMetric(this.selectedMetric); }
    },
    async addTask() {
      try {
        await axios.post('/api/metrictasktypes/', { metric: this.selectedMetric, task_type: this.newTask.task_type, task_order: this.workflowTasks.length + 1 });
        this.closeAddTaskModal(); this.selectMetric(this.selectedMetric); this.fetchAllMetricTaskTypes();
      } catch (e) { console.error(e); }
    },
    async removeTask(task) {
      const ok = await this.showConfirm({ title: 'Remove task', message: `Remove "${task.task_type_name}" from this workflow?`, variant: 'danger', confirmLabel: 'Remove' });
      if (!ok) return;
      try { await axios.delete(`/api/metrictasktypes/${task.id}/`); this.selectMetric(this.selectedMetric); this.fetchAllMetricTaskTypes(); } catch (e) { console.error(e); }
    },
    async cloneWorkflow() {
      try { await axios.post('/api/metrictasktypes/clone/', { source_metric_id: this.cloneSourceMetric, target_metric_id: this.selectedMetric }); this.closeCloneModal(); this.selectMetric(this.selectedMetric); this.fetchAllMetricTaskTypes(); } catch (e) { console.error(e); }
    },
    async handleWorkReorder() {
      this.workTemplates.forEach((t, i) => { t.display_order = i + 1; });
      try { await axios.post('/api/opportunityworktemplates/reorder/', { metric_id: this.selectedMetric, template_orders: this.workTemplates.map(t => ({ id: t.id, order: t.display_order })) }); }
      catch (e) { console.error(e); this.selectMetric(this.selectedMetric); }
    },
    async addWorkTemplate() {
      try {
        await axios.post('/api/opportunityworktemplates/', { metric: this.selectedMetric, work_type: this.newWorkTemplate.work_type, is_required: this.newWorkTemplate.is_required, display_order: this.workTemplates.length + 1 });
        this.closeAddWorkModal(); this.selectMetric(this.selectedMetric);
      } catch (e) { console.error(e); }
    },
    async updateWorkTemplate(template) {
      try { await axios.patch(`/api/opportunityworktemplates/${template.id}/`, { is_required: template.is_required }); } catch (e) { console.error(e); this.selectMetric(this.selectedMetric); }
    },
    async removeWorkTemplate(template) {
      const name = template.work_type_details?.display_name || template.work_type_details?.name;
      const ok = await this.showConfirm({ title: 'Remove work template', message: `Remove "${name}" from work templates?`, variant: 'danger', confirmLabel: 'Remove' });
      if (!ok) return;
      try { await axios.delete(`/api/opportunityworktemplates/${template.id}/`); this.selectMetric(this.selectedMetric); } catch (e) { console.error(e); }
    },
    async addMetricWorkflow() {
      try {
        for (let i = 0; i < this.addMetricTaskTypes.length; i++) {
          await axios.post('/api/metrictasktypes/', { metric: this.addMetricId, task_type: this.addMetricTaskTypes[i], task_order: i + 1 });
        }
        const id = this.addMetricId;
        this.closeAddMetricsModal();
        await this.fetchAllMetricTaskTypes();
        this.openMetric(id);
      } catch (e) { console.error(e); }
    },
    async deleteMetricWorkflow() {
      const metricId = this.confirmDeleteMetric.id;
      try {
        const { data } = await axios.get('/api/metrictasktypes/', { params: { metric: metricId } });
        await Promise.all(data.map(mtt => axios.delete(`/api/metrictasktypes/${mtt.id}/`)));
        const wasSelected = this.selectedMetric === metricId;
        if (wasSelected) this.clearMetricSelection();
        this.confirmDeleteMetric = null; this.fetchAllMetricTaskTypes();
        if (wasSelected) {
          this.$router.replace({ name: 'AdminWorkflowMetricWorkflows' });
        }
      } catch (e) { console.error(e); }
    },

    closeAddMetricsModal() { this.showAddMetricsModal = false; this.addMetricId = null; this.addMetricTaskTypes = []; },
    async createAndAddTask() {
      try {
        const { data } = await axios.post('/api/tasktypes/', this.newTaskType);
        this.taskTypes.push(data);
        await axios.post('/api/metrictasktypes/', { metric: this.selectedMetric, task_type: data.id, task_order: this.workflowTasks.length + 1 });
        this.closeAddTaskModal();
        this.selectMetric(this.selectedMetric);
        this.fetchAllMetricTaskTypes();
      } catch (e) { console.error(e); this.showToast('Error creating task type', e.response?.data?.detail); }
    },
    closeAddTaskModal() { this.showAddTaskModal = false; this.newTask = { task_type: null }; this.addTaskMode = 'existing'; this.newTaskType = { name: '', color: '#3B82F6', icon: 'tasks' }; },
    closeCloneModal() { this.showCloneModal = false; this.cloneSourceMetric = null; },
    closeAddWorkModal() { this.showAddWorkModal = false; this.newWorkTemplate = { work_type: null, is_required: true }; },
  },

  mounted() {
    this.fetchMetrics();
    this.fetchTaskTypes();
    this.fetchWorkTypes();
    this.fetchAllMetricTaskTypes();
    this.fetchInstructionTypes();
  },
};
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }

.shared-badge .shared-tooltip {
  display: none;
  position: absolute;
  left: 50%;
  bottom: calc(100% + 6px);
  transform: translateX(-50%);
  width: max-content;
  max-width: 260px;
  padding: 8px 10px;
  font-size: 11px;
  line-height: 1.4;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
  text-align: left;
  white-space: normal;
}
.shared-badge .shared-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #fde68a;
}
.shared-badge:hover .shared-tooltip { display: block; }
</style>
